import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const LAST_UPDATED = "July 21, 2026";

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="text-lg md:text-lg font-black font-display text-slate-900">{title}</h2>
      <div className="mt-2 space-y-3 text-sm text-slate-600 leading-relaxed">{children}</div>
    </section>
  );
}

export default function Privacy() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-3xl px-6 py-12">
        <Link
          to="/login"
          data-testid="privacy-back-link"
          className="inline-flex items-center gap-2 text-sm font-semibold text-purple-700 hover:text-purple-900"
        >
          <ArrowLeft className="w-4 h-4" /> Back
        </Link>

        <div className="mt-6 flex items-center gap-4">
          <img src="/cfaa-mark.png" alt="CFAA" className="h-14 w-auto rounded-lg" />
          <div>
            <h1 className="text-4xl sm:text-5xl font-black font-display tracking-tight text-slate-900">Privacy Policy</h1>
            <p className="mt-1 text-sm text-slate-500">Crusader Football Alumni Association (CFAA)</p>
          </div>
        </div>
        <p className="mt-4 text-xs uppercase tracking-widest text-slate-400 font-bold">Last updated: {LAST_UPDATED}</p>

        <div className="mt-6 rounded-2xl bg-white border border-slate-100 shadow-sm p-6 md:p-8">
          <p className="text-sm text-slate-600 leading-relaxed">
            This Privacy Policy explains how the Crusader Football Alumni Association ("CFAA", "we", "us", or "our")
            collects, uses, and protects your information when you use our membership and community app (the "App"),
            which includes member profiles, group and private chat, announcements, events, and membership dues.
            By creating an account or using the App, you agree to this Policy.
          </p>

          <Section title="1. Information We Collect">
            <p><strong>Account information you provide:</strong> first and last name, email address, phone number, the years you played, a profile photo (optional), and your password (stored only in encrypted/hashed form).</p>
            <p><strong>Content you create:</strong> chat messages, direct messages, poll votes, event RSVPs, announcement posts, uploaded images or files, and reactions/likes.</p>
            <p><strong>Membership &amp; payment information:</strong> your membership tier, renewal status, and payment history. Card payments are processed securely by our payment provider (Stripe). We do <strong>not</strong> store your full debit/credit card number on our servers.</p>
            <p><strong>Technical information:</strong> basic device and log data (such as access times and error logs) used to operate and secure the App.</p>
            <p><strong>Authentication:</strong> if you sign in with Google, we receive your name, email, and profile picture from Google to create your account.</p>
          </Section>

          <Section title="2. How We Use Your Information">
            <ul className="list-disc pl-5 space-y-1">
              <li>Create and manage your account and membership.</li>
              <li>Enable community features: chat, direct messages, announcements, events, and polls.</li>
              <li>Process membership dues and donations, and manage renewals and access.</li>
              <li>Send you service-related notifications (for example, new messages or event updates).</li>
              <li>Maintain the security, integrity, and proper functioning of the App.</li>
            </ul>
          </Section>

          <Section title="3. How We Share Information">
            <p>We do not sell your personal information. We share information only:</p>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>With other members:</strong> your name, profile photo, and any content you post are visible to other approved members according to your role and access level.</li>
              <li><strong>With service providers</strong> who help us operate the App, including our payment processor (Stripe), hosting/infrastructure providers, and authentication providers (e.g., Google). These providers process data on our behalf.</li>
              <li><strong>For legal reasons</strong> if required by law, regulation, or valid legal process, or to protect the rights and safety of our members.</li>
            </ul>
          </Section>

          <Section title="4. Payments">
            <p>
              Membership dues, donations, and related payments are handled by Stripe. Your payment card details are
              entered on and processed by Stripe and are subject to Stripe's own privacy policy. We receive only
              limited information such as payment status and amount to record your membership and history.
            </p>
          </Section>

          <Section title="5. Data Retention">
            <p>
              We keep your information for as long as your account is active or as needed to provide the App and meet
              legal, accounting, or reporting obligations. You may request deletion of your account and associated
              personal data at any time (see "Your Rights" below).
            </p>
          </Section>

          <Section title="6. Your Rights &amp; Choices">
            <ul className="list-disc pl-5 space-y-1">
              <li>Access, update, or correct your profile information in the App.</li>
              <li>Request a copy or deletion of your personal data.</li>
              <li>Delete messages you have posted, where permitted.</li>
              <li>Opt out of non-essential notifications.</li>
              <li>Close your account by contacting us.</li>
            </ul>
          </Section>

          <Section title="7. Data Security">
            <p>
              We use industry-standard measures to protect your information, including encrypted passwords, secure
              (HTTPS) connections, and access controls based on member roles. No method of transmission or storage is
              100% secure, but we work to protect your data and promptly address any issues.
            </p>
          </Section>

          <Section title="8. Children's Privacy">
            <p>
              The App is intended for alumni and adult members and is not directed to children under 13. We do not
              knowingly collect personal information from children under 13. If you believe a child has provided us
              information, please contact us and we will delete it.
            </p>
          </Section>

          <Section title="9. Changes to This Policy">
            <p>
              We may update this Privacy Policy from time to time. When we do, we will revise the "Last updated" date
              above. Your continued use of the App after changes take effect means you accept the updated Policy.
            </p>
          </Section>

          <Section title="10. Contact Us">
            <p>
              If you have questions about this Privacy Policy or your data, please contact the CFAA administrators at{" "}
              <a href="mailto:pete.medlock7@gmail.com" className="text-purple-700 font-semibold hover:underline" data-testid="privacy-contact-email">pete.medlock7@gmail.com</a>.
            </p>
          </Section>
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          &copy; {new Date().getFullYear()} Crusader Football Alumni Association. All rights reserved.{" "}
          <Link to="/terms" data-testid="privacy-to-terms" className="font-semibold text-purple-700 hover:underline">Terms of Service</Link>
        </p>
      </div>
    </div>
  );
}
